import { hasMcpLastCallStamp, mcpCallsAreCounted } from '../mcpEndpointCallHonesty';
import type {
  KongCatalogRoute,
  McpEndpointRow,
  McpHealthStatus,
  McpProbeBoardCounts,
  ObserveStatRow,
  AppObserveAggregate,
  ServiceComponent,
  ServiceStatus,
} from './types';

// TODO: api_telemetry http_status/processing_time_ms null on prod until api-manager write path is fixed

export const EXPOSE_SERVICE_KEYS = [
  'kong',
  'orchestrator-api',
  'api-manager',
  'telemetry-read',
  'agent-registry',
  'volcano-engine',
] as const;

const KONG_SERVICE_TO_COMPONENT: Record<string, string> = {
  orchestrator_svc: 'kong',
  orchestrator_upstream: 'kong',
  orchestrator_status_svc: 'kong',
  telemetry_read_svc: 'telemetry-read',
  telemetry_read_upstream: 'telemetry-read',
  api_manager_svc: 'api-manager',
  api_manager_upstream: 'api-manager',
  iot_shack_svc: 'iot-shack',
  agent_registry_upstream: 'agent-registry',
  volcano_engine_upstream: 'volcano-engine',
  asset_app_svc: 'asset-app-api',
  asset_app_upstream: 'asset-app-api',
};

export function resolveComponentKey(
  kongServiceName: string | undefined,
  components: Record<string, ServiceComponent>
): { key: string | null; component: ServiceComponent | null } {
  const raw = String(kongServiceName || '').toLowerCase();
  const mapped = KONG_SERVICE_TO_COMPONENT[raw];
  if (mapped) return { key: mapped, component: components[mapped] ?? null };

  for (const [key, comp] of Object.entries(components)) {
    if (raw.includes(key.replace(/-/g, '_'))) return { key, component: comp };
  }
  if (raw.includes('orchestrator')) return { key: 'orchestrator-api', component: components.kong ?? null };
  if (raw.includes('telemetry')) return { key: 'telemetry-read', component: components['telemetry-read'] ?? null };
  if (raw.includes('api_manager') || raw.includes('api-manager')) {
    return { key: 'api-manager', component: components['api-manager'] ?? null };
  }
  return { key: null, component: null };
}

/** Honest last-probed cell: real observation, or why this route is unprobed. Never invents ok. */
export function presentRouteLastProbed(
  component: ServiceComponent | null,
  componentKey: string | null
): string {
  if (component) return formatSecondsAgo(component.observed_at || null);
  if (componentKey) return `no ${componentKey} in this cluster`;
  return 'no matching /services/status component';
}

export function isExposeRoutePath(path: string): boolean {
  const normalized = String(path || '').replace(/^~/, '');
  return /\/public\//i.test(normalized) || /\/mcp\/app/i.test(normalized);
}

export function filterExposeRoutes(routes: KongCatalogRoute[]): KongCatalogRoute[] {
  return routes.filter((r) => (r.paths || []).some(isExposeRoutePath));
}

export function statusToChipColor(
  status: ServiceStatus | 'unknown'
): 'success' | 'warning' | 'error' | 'default' {
  if (status === 'ok') return 'success';
  if (status === 'degraded') return 'warning';
  if (status === 'down') return 'error';
  return 'default';
}

export function formatSecondsAgo(isoOrMs: string | number | null): string {
  if (isoOrMs == null) return '—';
  const ts = typeof isoOrMs === 'number' ? isoOrMs : new Date(isoOrMs).getTime();
  if (!Number.isFinite(ts)) return '—';
  const sec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return `${Math.floor(sec / 3600)}h ago`;
}

export function aggregateStatsByApp(stats: ObserveStatRow[]): Map<string, AppObserveAggregate> {
  const byApp = new Map<string, ObserveStatRow[]>();
  for (const row of stats) {
    const appId = String(row.connection_id || '').split(':')[0];
    if (!appId) continue;
    const list = byApp.get(appId) || [];
    list.push(row);
    byApp.set(appId, list);
  }

  const out = new Map<string, AppObserveAggregate>();
  for (const [appId, rows] of byApp) {
    let totalCalls = 0;
    let errorWeighted = 0;
    let errorRateSamples = 0;
    let latencyWeighted = 0;
    let latencyCalls = 0;
    let primary: ObserveStatRow | null = null;

    for (const row of rows) {
      const calls = Number(row.call_count || 0);
      totalCalls += calls;
      if (row.error_rate_pct != null && calls > 0) {
        errorWeighted += row.error_rate_pct * calls;
        errorRateSamples += calls;
      }
      if (row.avg_latency_ms != null && calls > 0) {
        latencyWeighted += row.avg_latency_ms * calls;
        latencyCalls += calls;
      }
      if (!primary || calls > Number(primary.call_count || 0)) primary = row;
    }

    out.set(appId, {
      error_rate_24h: errorRateSamples > 0 ? errorWeighted / errorRateSamples : null,
      avg_latency_ms: latencyCalls > 0 ? latencyWeighted / latencyCalls : null,
      primary_connection_id: primary?.connection_id ?? null,
      tool_stats: rows.sort((a, b) => Number(b.call_count || 0) - Number(a.call_count || 0)),
    });
  }
  return out;
}

/**
 * Tenant-wide MCP last-call probe — not per-app public-route health.
 * Never / Unknown / Not provisioned are not Critical. Critical is a real
 * observed failure (error rate), not an absent last-call stamp.
 */
export function deriveMcpHealthStatus(
  ep: McpEndpointRow,
  agg: AppObserveAggregate | undefined
): McpHealthStatus {
  if (!ep.mcp_provisioned) return 'not_provisioned';
  if (!mcpCallsAreCounted(ep)) return 'unknown';
  if (!hasMcpLastCallStamp(ep)) return 'never';

  const lastMs = new Date(ep.last_mcp_call as string).getTime();
  const ageMin = Number.isFinite(lastMs) ? (Date.now() - lastMs) / 60_000 : Infinity;
  const errorRate = agg?.error_rate_24h ?? null;

  if (errorRate != null && errorRate > 20) return 'critical';
  if (errorRate != null && errorRate >= 5) return 'warning';
  if (ageMin > 30) return 'quiet';
  if (ageMin <= 30) return 'healthy';
  return 'unknown';
}

export function mcpHealthLabel(status: McpHealthStatus): string {
  switch (status) {
    case 'healthy':
      return 'Active';
    case 'warning':
      return 'Degraded';
    case 'quiet':
      return 'Quiet';
    case 'critical':
      return 'Failed';
    case 'never':
      return 'Never';
    case 'not_provisioned':
      return 'Not provisioned';
    default:
      return 'Unknown';
  }
}

export function summarizeMcpProbeBoard(statuses: McpHealthStatus[]): McpProbeBoardCounts {
  const counts: McpProbeBoardCounts = {
    failed: 0,
    active: 0,
    quiet: 0,
    degraded: 0,
    never: 0,
    unknown: 0,
    not_provisioned: 0,
  };
  for (const status of statuses) {
    if (status === 'critical') counts.failed += 1;
    else if (status === 'healthy') counts.active += 1;
    else if (status === 'quiet') counts.quiet += 1;
    else if (status === 'warning') counts.degraded += 1;
    else if (status === 'never') counts.never += 1;
    else if (status === 'not_provisioned') counts.not_provisioned += 1;
    else counts.unknown += 1;
  }
  return counts;
}

export function formatNaN(value: number | null | undefined, suffix = ''): string {
  if (value == null || Number.isNaN(value)) return 'N/A';
  if (suffix === '%') return `${Number(value).toFixed(1)}%`;
  if (suffix === 'ms') return `${Math.round(Number(value))}ms`;
  return `${Number(value)}${suffix}`;
}

export function resolveAppName(appId: string | null, endpoints: McpEndpointRow[]): string {
  if (!appId) return '—';
  const ep = endpoints.find((e) => e.app_id === appId);
  return ep?.name || appId;
}
